import Document from "documents/document";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import GetAppIcon from "@mui/icons-material/GetApp";
import InfoIcon from "@mui/icons-material/Info";
import UploadIcon from "@mui/icons-material/Upload";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import axios, { HttpStatusCode } from "axios";
import LoadingButton from "_shared/_views/loading/loading-button";
import Guid from "_shared/guids/guid";
import { enqueueSnackbar } from "notistack";
import React from "react";
import { defaultStyles, FileIcon } from "react-file-icon";
import { useSession } from "users/__session/session-context";
import CommentsButton from "work/proposals/comments/_view/comments-button";
import CommentThread from "work/proposals/comments/threads/comment-thread";
import WorkDocument from "work/work-agreements/contracts/work-document";
import DocumentAPIService from "documents/_api/document-api-service";
import DocumentTopic from "documents/topics/document-topic";
import UnavailableDocument from "documents/types/unavailable-documents/unavailable-document";
import { WorkDocumentType } from "work/work-agreements/contracts/types";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import { DocumentFileType } from "documents/types/document-file-type";
import { enumKeyOf } from "_shared/_utilities/enum-utils";

const DocumentList = styled(List)(() => ({
  "&.MuiList-root": {
    marginLeft: 0,
  },
  minWidth: "20rem",
}));
const ListSubhead = styled(ListSubheader)(() => ({
  backgroundColor: "#FFF",
  color: "#000",
  display: "flex",
  fontSize: "1.4em",
  justifyContent: "space-between",
  paddingRight: 0,
  whiteSpace: "nowrap",
}));
const PoliciesHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  paddingRight: theme.spacing(2),
  width: "100%",
}));
const NoRowsPlaceholder = styled(Typography)(({ theme }) => ({
  fontSize: "1.1em",
  paddingTop: theme.spacing(1),
}));
const DocumentIcon = styled(ListItemAvatar)(({ theme }) => ({
  marginRight: theme.spacing(1),
  minWidth: "2rem",
  maxWidth: "2rem",
}));

type SelectedDocumentListProps = {
  className?: string;
  documents?: WorkDocument[];
  documentType: WorkDocumentType;
  downloadingFileId: Guid | null;
  commentsToolTip: string;
  commentThreads?: CommentThread[];
  disableComments?: boolean;
  disableEditing?: boolean;
  conflictsCheckWaived?: boolean;
  isSaved?: boolean;
  activeReviewField?: ProposalField;
  onCommentsClicked?: (field: ProposalField, name?: string) => void;
  onDownload: (documentId: Guid) => void;
  onDocumentRemoved: (documentId: Guid) => void;
  onDocumentAdded: (document: Document) => void;
};

export default function SelectedDocumentList(props: Readonly<SelectedDocumentListProps>) {
  const {
    className,
    documents,
    documentType,
    downloadingFileId,
    commentsToolTip,
    commentThreads,
    disableComments,
    disableEditing,
    conflictsCheckWaived,
    isSaved,
    activeReviewField,
    onCommentsClicked,
    onDownload,
    onDocumentRemoved,
    onDocumentAdded,
  } = props;

  const [isUploading, setIsUploading] = React.useState<boolean>(false);

  const session = useSession();
  const documentFileUploaderRef = React.useRef<HTMLInputElement | null>(null);

  function renderActionButtons(document: Document) {
    let field = ProposalField.Conflicts;
    if (documentType === WorkDocumentType.Conflicts) {
      field = ProposalField.ConflictsDocument(document.id);
    } else if (documentType === WorkDocumentType.ClientPolicy) {
      field = ProposalField.ClientPolicy(document.id);
    } else if (documentType === WorkDocumentType.VendorPolicy) {
      field = ProposalField.VendorPolicy(document.id);
    }
    return (
      <>
        <Tooltip title="Download Document">
          <span>
            <IconButton
              edge="end"
              disabled={downloadingFileId !== null}
              onClick={() => onDownload(document.id)}
              size="medium"
            >
              {downloadingFileId?.isEqualTo(document.id) ? (
                <CircularProgress color="primary" size={24} disableShrink />
              ) : (
                <GetAppIcon color="primary" />
              )}
            </IconButton>
          </span>
        </Tooltip>
        {!disableEditing && (
          <Tooltip title="Remove Document">
            <span>
              <IconButton
                edge="end"
                disabled={downloadingFileId !== null}
                onClick={() => onDocumentRemoved(document.id)}
                size="medium"
              >
                <DeleteIcon color="error" />
              </IconButton>
            </span>
          </Tooltip>
        )}
        <CommentsButton
          commentThreads={commentThreads}
          disabled={disableComments}
          field={field}
          isActive={activeReviewField?.isEqualTo(field)}
          toolTip={commentsToolTip}
          onCommentsClicked={() => onCommentsClicked?.(field, document.name?.value)}
        />
      </>
    );
  }

  async function handleDocumentFileUpload(event: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    if (!event.currentTarget.files) return;

    const file: File = event.currentTarget.files[0];

    try {
      setIsUploading(true);

      let context: string | undefined;
      context = documentType === WorkDocumentType.Conflicts ? "Conflicts" : "Policy";
      const service = new DocumentAPIService(session);
      const document = await service.createDocument(file, file.name, [
        DocumentTopic.create({
          context: context,
        }),
      ]);
      if (!(document instanceof Document)) {
        console.error("Uploaded document is not available.");
        throw new Error("Uploaded document is not available.");
      }
      enqueueSnackbar("Uploaded file", { variant: "success" });
      onDocumentAdded(WorkDocument.fromDocument(document, documentType));
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === HttpStatusCode.UnsupportedMediaType) {
          enqueueSnackbar(error.response?.data, { variant: "error" });
        } else if (error.response?.status === HttpStatusCode.UnprocessableEntity) {
          enqueueSnackbar("Unsupported file type", { variant: "error" });
        } else {
          enqueueSnackbar("Unknown error occurred during upload", { variant: "error" });
        }
      }
    } finally {
      event.target.value = "";
      setIsUploading(false);
    }
  }

  return (
    <>
      <DocumentList disablePadding className={className}>
        <ListSubhead>
          {documentType === WorkDocumentType.Conflicts && (
            <span>
              Your Conflicts Checks
              <Tooltip
                disableFocusListener
                title="Conflicts document(s) establish potential conflicts checks required by participating clients/vendors"
                placement="bottom"
                arrow
              >
                <IconButton size="large" edge="end">
                  <InfoIcon />
                </IconButton>
              </Tooltip>
            </span>
          )}
          {(documentType === WorkDocumentType.ClientPolicy || documentType === WorkDocumentType.VendorPolicy) && (
            <PoliciesHeader>
              <span>
                Your Policies
                <Tooltip
                  disableFocusListener
                  title="Policy document(s) establish the policies required by participating clients/vendors"
                  placement="bottom"
                  arrow
                >
                  <IconButton size="large" edge="end">
                    <InfoIcon />
                  </IconButton>
                </Tooltip>
              </span>
              <CommentsButton
                field={documentType === WorkDocumentType.ClientPolicy ? ProposalField.ClientPolicies : ProposalField.VendorPolicies}
                isActive={activeReviewField?.isEqualTo(documentType === WorkDocumentType.ClientPolicy ? ProposalField.ClientPolicies : ProposalField.VendorPolicies)}
                toolTip={commentsToolTip}
                commentThreads={commentThreads}
                disabled={!isSaved}
                onCommentsClicked={() => {
                  return onCommentsClicked?.(
                    documentType === WorkDocumentType.ClientPolicy
                      ? ProposalField.ClientPolicies
                      : ProposalField.VendorPolicies
                  )
                }}
              />
            </PoliciesHeader>
          )}
        </ListSubhead>
        {!disableEditing && (
          <ListItem key="uploadButton" sx={{ pt: 0 }}>
            <LoadingButton
              color="primary"
              loading={isUploading}
              startIcon={<UploadIcon />}
              onClick={() => documentFileUploaderRef.current?.click()}
            >
              {`Upload ${documentType === WorkDocumentType.Conflicts ? "Conflicts Check" : "Policy Document"}`}
            </LoadingButton>
          </ListItem>
        )}
        {!conflictsCheckWaived && (!documents || documents.length < 1) && (
          <ListItem key="noDocuments">
            <NoRowsPlaceholder color="textDisabled">None Selected</NoRowsPlaceholder>
          </ListItem>
        )}
        {!conflictsCheckWaived &&
          documents
            ?.filter((document) => !(document instanceof UnavailableDocument))
            .map((document: Document | UnavailableDocument) => {
              const availableDocument = document as Document;
              return (
                <ListItem
                  key={document.id.value as React.Key}
                  secondaryAction={renderActionButtons(availableDocument)}
                >
                  <DocumentIcon>
                    {document instanceof UnavailableDocument ? (
                      <FileIcon />
                    ) : (
                      <FileIcon
                        extension={enumKeyOf(DocumentFileType, document.fileType).toLocaleLowerCase()}
                        {...defaultStyles[enumKeyOf(DocumentFileType, document.fileType).toLocaleLowerCase() as keyof typeof defaultStyles]}
                      />
                    )}
                  </DocumentIcon>
                  <ListItemText
                    primary={availableDocument.name?.value}
                    secondary={availableDocument.created?.format("MM/DD/YY hh:mm A")}
                    slotProps={{
                      primary: {
                        sx: {
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          width: "75%",
                        }
                      },
                    }}
                  />
                </ListItem>
              );
            })}
      </DocumentList>
      <input type="file" hidden={true} ref={documentFileUploaderRef} onChange={handleDocumentFileUpload} />
    </>
  );
}
